import { useState, useEffect } from "react";
import axios from "axios";
import {
  BarChart3, Users, CreditCard, TrendingUp, Phone, Clock,
  ChevronDown, ChevronUp, XCircle, PlusCircle, Activity,
  Zap, Crown, Sparkles, Tag, Plus, Trash2, Edit, Eye, EyeOff, Cpu, Save, X
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

function StatCard({ icon: Icon, label, value, sub, color = "text-black" }) {
  return (
    <div className="bg-white border border-gray-200 p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 bg-gray-100 flex items-center justify-center">
          <Icon size={18} className="text-gray-500" />
        </div>
        <span className="text-xs text-gray-500 font-dm-sans uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-2xl font-bold font-outfit ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1 font-dm-sans">{sub}</p>}
    </div>
  );
}

function MiniBar({ data, maxKey = "revenue" }) {
  const maxVal = Math.max(...data.map(d => d[maxKey] || 0), 1);
  return (
    <div className="flex items-end gap-1 h-20">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center">
          <div
            className="w-full bg-black/80 rounded-t-sm min-h-[2px] transition-all"
            style={{ height: `${Math.max(((d[maxKey] || 0) / maxVal) * 100, 2)}%` }}
            title={`${d.date}: ₹${d[maxKey] || 0}`}
          />
          {i % 3 === 0 && <span className="text-[8px] text-gray-400 mt-1">{d.date?.split(" ")[1]}</span>}
        </div>
      ))}
    </div>
  );
}

export default function AdminAITools() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [catalogTools, setCatalogTools] = useState([]);
  const [editingTool, setEditingTool] = useState(null);
  const [showToolForm, setShowToolForm] = useState(false);
  const [toolForm, setToolForm] = useState({ name: "", tagline: "", description: "", category: "Analytics", status: "coming-soon", tool_url: "", features: [{ title: "", description: "" }], use_cases: [""] });
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [couponForm, setCouponForm] = useState({ code: "", discount_percentage: 10, max_uses: "", valid_until: "", applicable_plans: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, subsRes, couponsRes, toolsRes] = await Promise.all([
        axios.get(`${API}/subscriptions/admin/stats`, authHeader()),
        axios.get(`${API}/subscriptions/admin/all`, authHeader()),
        axios.get(`${API}/subscriptions/admin/coupons`, authHeader()),
        axios.get(`${API}/ai-tools-catalog/admin`, authHeader())
      ]);
      setStats(statsRes.data);
      setSubscriptions(subsRes.data);
      setCoupons(couponsRes.data);
      setCatalogTools(toolsRes.data);
    } catch (err) {
      console.error("Failed to fetch AI Tools data:", err);
    } finally {
      setLoading(false);
    }
  };

  const cancelSubscription = async (userId) => {
    if (!window.confirm("Cancel this subscription?")) return;
    try {
      await axios.post(`${API}/subscriptions/admin/cancel/${userId}`, {}, authHeader());
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to cancel");
    }
  };

  const extendSubscription = async (userId) => {
    const days = prompt("Extend by how many days?", "30");
    if (!days) return;
    try {
      await axios.post(`${API}/subscriptions/admin/extend/${userId}?days=${days}`, {}, authHeader());
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to extend");
    }
  };

  const createCoupon = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/subscriptions/admin/coupons`, {
        code: couponForm.code,
        discount_percentage: parseInt(couponForm.discount_percentage),
        max_uses: couponForm.max_uses ? parseInt(couponForm.max_uses) : null,
        valid_until: couponForm.valid_until || null,
        applicable_plans: couponForm.applicable_plans.length > 0 ? couponForm.applicable_plans : null,
        is_active: true
      }, authHeader());
      setCouponForm({ code: "", discount_percentage: 10, max_uses: "", valid_until: "", applicable_plans: [] });
      setShowCouponForm(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create coupon");
    }
  };

  const toggleCouponActive = async (couponId, currentActive) => {
    try {
      await axios.put(`${API}/subscriptions/admin/coupons/${couponId}`, { is_active: !currentActive }, authHeader());
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update coupon");
    }
  };

  const deleteCoupon = async (couponId) => {
    if (!window.confirm("Delete this coupon?")) return;
    try {
      await axios.delete(`${API}/subscriptions/admin/coupons/${couponId}`, authHeader());
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete coupon");
    }
  };

  const togglePlan = (plan) => {
    setCouponForm(prev => ({
      ...prev,
      applicable_plans: prev.applicable_plans.includes(plan)
        ? prev.applicable_plans.filter(p => p !== plan)
        : [...prev.applicable_plans, plan]
    }));
  };

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "tools", label: "Tools Catalog", icon: Cpu },
    { id: "subscriptions", label: "Subscriptions", icon: CreditCard },
    { id: "coupons", label: "Coupons", icon: Tag },
    { id: "usage", label: "Usage & Users", icon: Activity },
  ];

  const resetToolForm = () => {
    setToolForm({ name: "", tagline: "", description: "", category: "Analytics", status: "coming-soon", tool_url: "", features: [{ title: "", description: "" }], use_cases: [""] });
    setEditingTool(null);
  };

  const openEditTool = (tool) => {
    setToolForm({
      name: tool.name || "",
      tagline: tool.tagline || "",
      description: tool.description || "",
      category: tool.category || "Analytics",
      status: tool.status || "coming-soon",
      tool_url: tool.tool_url || "",
      features: tool.features?.length > 0 ? tool.features : [{ title: "", description: "" }],
      use_cases: tool.use_cases?.length > 0 ? tool.use_cases : [""]
    });
    setEditingTool(tool.id);
    setShowToolForm(true);
  };

  const saveTool = async (e) => {
    e.preventDefault();
    const data = {
      ...toolForm,
      features: toolForm.features.filter(f => f.title.trim()),
      use_cases: toolForm.use_cases.filter(u => u.trim())
    };
    try {
      if (editingTool) {
        await axios.put(`${API}/ai-tools-catalog/admin/${editingTool}`, data, authHeader());
      } else {
        await axios.post(`${API}/ai-tools-catalog/admin`, data, authHeader());
      }
      setShowToolForm(false);
      resetToolForm();
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save tool");
    }
  };

  const deleteTool = async (toolId) => {
    if (!window.confirm("Delete this AI tool?")) return;
    try {
      await axios.delete(`${API}/ai-tools-catalog/admin/${toolId}`, authHeader());
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete");
    }
  };

  const toggleToolVisibility = async (tool) => {
    try {
      await axios.put(`${API}/ai-tools-catalog/admin/${tool.id}`, { is_visible: !tool.is_visible }, authHeader());
      fetchData();
    } catch {}
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-black border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-ai-tools">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold font-outfit text-gray-900">AI Tools Management</h1>
        <p className="text-sm text-gray-500 font-dm-sans mt-1">Manage subscriptions, monitor usage, and track revenue</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 w-fit" data-testid="ai-tools-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium font-dm-sans transition-all ${
              activeTab === tab.id ? "bg-white text-black shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={15} /> {tab.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === "dashboard" && stats && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={CreditCard} label="Active Subscriptions" value={stats.active_subscriptions} />
            <StatCard icon={TrendingUp} label="Total Revenue" value={`₹${(stats.total_revenue || 0).toLocaleString()}`} />
            <StatCard
              icon={TrendingUp}
              label="This Month"
              value={`₹${(stats.month_revenue || 0).toLocaleString()}`}
              sub={stats.revenue_growth_pct > 0 ? `+${stats.revenue_growth_pct}% vs last month` : stats.revenue_growth_pct < 0 ? `${stats.revenue_growth_pct}% vs last month` : "—"}
            />
            <StatCard icon={Users} label="Total Users" value={stats.total_users || 0} />
          </div>

          {/* Usage Metrics */}
          <div className="grid grid-cols-3 gap-4">
            <StatCard icon={Phone} label="Total Analyses" value={stats.total_analyses || 0} />
            <StatCard icon={Activity} label="Today" value={stats.today_analyses || 0} />
            <StatCard icon={BarChart3} label="This Month" value={stats.month_analyses || 0} />
          </div>

          {/* Revenue Chart */}
          {stats.daily_revenue?.length > 0 && (
            <div className="bg-white border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-900 font-outfit mb-4">Revenue (Last 14 Days)</h3>
              <MiniBar data={stats.daily_revenue} />
            </div>
          )}

          {/* Plan Breakdown */}
          <div className="bg-white border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 font-outfit mb-4">Plan Breakdown</h3>
            <div className="grid grid-cols-3 gap-4">
              {(stats.plan_breakdown || []).map(p => {
                const icons = { starter: Zap, professional: Crown, enterprise: Sparkles };
                const Icon = icons[p.plan] || Zap;
                return (
                  <div key={p.plan} className="flex items-center gap-3 p-3 bg-gray-50">
                    <Icon size={18} className="text-gray-500" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900 capitalize font-outfit">{p.plan}</p>
                      <p className="text-xs text-gray-500 font-dm-sans">{p.active_count} active</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Recent Subscriptions */}
            <div className="bg-white border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-900 font-outfit mb-3">Recent Subscriptions</h3>
              {(stats.recent_subscriptions || []).length === 0 ? (
                <p className="text-gray-400 text-sm font-dm-sans">No subscriptions yet</p>
              ) : (
                <div className="space-y-2">
                  {stats.recent_subscriptions.slice(0, 5).map((s, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <div>
                        <p className="text-sm text-gray-900 font-dm-sans">{s.user_email}</p>
                        <p className="text-xs text-gray-400">{s.plan_name} · {s.billing_cycle}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 ${s.status === "active" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                        {s.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Analyses */}
            <div className="bg-white border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-900 font-outfit mb-3">Recent Analyses</h3>
              {(stats.recent_analyses || []).length === 0 ? (
                <p className="text-gray-400 text-sm font-dm-sans">No analyses yet</p>
              ) : (
                <div className="space-y-2">
                  {stats.recent_analyses.slice(0, 5).map((a, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <div>
                        <p className="text-sm text-gray-900 font-dm-sans">{a.filename || "Recording"}</p>
                        <p className="text-xs text-gray-400">{a.analysis_id} · {a.created_at?.split("T")[0]}</p>
                      </div>
                      <span className="text-xs text-gray-400">{a.user_id ? "User" : "Free"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Tools Catalog Tab */}
      {activeTab === "tools" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 font-outfit">AI Tools ({catalogTools.length})</h3>
            <button
              onClick={() => { resetToolForm(); setShowToolForm(!showToolForm); }}
              className="flex items-center gap-1.5 px-3 py-2 bg-black text-white text-xs font-medium hover:bg-gray-800 transition-colors"
              data-testid="add-tool-btn"
            >
              <Plus size={14} /> Add Tool
            </button>
          </div>

          {/* Tool Form */}
          {showToolForm && (
            <form onSubmit={saveTool} className="bg-white border border-gray-200 p-5 space-y-4">
              <h4 className="text-sm font-semibold text-gray-900 font-outfit">{editingTool ? "Edit Tool" : "Add New Tool"}</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1 font-dm-sans">Name *</label>
                  <input type="text" value={toolForm.name} onChange={e => setToolForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full border border-gray-300 px-3 py-2 text-sm font-dm-sans outline-none focus:border-black" required data-testid="tool-name-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1 font-dm-sans">Category</label>
                  <select value={toolForm.category} onChange={e => setToolForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full border border-gray-300 px-3 py-2 text-sm font-dm-sans outline-none focus:border-black">
                    <option value="Analytics">Analytics</option>
                    <option value="Productivity">Productivity</option>
                    <option value="Content">Content</option>
                    <option value="Communication">Communication</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1 font-dm-sans">Tagline *</label>
                <input type="text" value={toolForm.tagline} onChange={e => setToolForm(f => ({ ...f, tagline: e.target.value }))}
                  className="w-full border border-gray-300 px-3 py-2 text-sm font-dm-sans outline-none focus:border-black" required data-testid="tool-tagline-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1 font-dm-sans">Description *</label>
                <textarea value={toolForm.description} onChange={e => setToolForm(f => ({ ...f, description: e.target.value }))} rows={3}
                  className="w-full border border-gray-300 px-3 py-2 text-sm font-dm-sans outline-none focus:border-black" required data-testid="tool-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1 font-dm-sans">Status</label>
                  <select value={toolForm.status} onChange={e => setToolForm(f => ({ ...f, status: e.target.value }))}
                    className="w-full border border-gray-300 px-3 py-2 text-sm font-dm-sans outline-none focus:border-black">
                    <option value="coming-soon">Coming Soon</option>
                    <option value="beta">Beta</option>
                    <option value="live">Live</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1 font-dm-sans">Tool URL (e.g., /ai-tools/call-analyzer)</label>
                  <input type="text" value={toolForm.tool_url} onChange={e => setToolForm(f => ({ ...f, tool_url: e.target.value }))}
                    placeholder="/ai-tools/..." className="w-full border border-gray-300 px-3 py-2 text-sm font-dm-sans outline-none focus:border-black" />
                </div>
              </div>

              {/* Features */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-600 font-dm-sans">Features</label>
                  <button type="button" onClick={() => setToolForm(f => ({ ...f, features: [...f.features, { title: "", description: "" }] }))}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"><Plus size={12} /> Add</button>
                </div>
                {toolForm.features.map((feat, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input type="text" placeholder="Feature title" value={feat.title} onChange={e => {
                      const f = [...toolForm.features]; f[i] = { ...f[i], title: e.target.value }; setToolForm(prev => ({ ...prev, features: f }));
                    }} className="flex-1 border border-gray-300 px-2 py-1.5 text-xs font-dm-sans outline-none focus:border-black" />
                    <input type="text" placeholder="Description" value={feat.description} onChange={e => {
                      const f = [...toolForm.features]; f[i] = { ...f[i], description: e.target.value }; setToolForm(prev => ({ ...prev, features: f }));
                    }} className="flex-[2] border border-gray-300 px-2 py-1.5 text-xs font-dm-sans outline-none focus:border-black" />
                    {toolForm.features.length > 1 && (
                      <button type="button" onClick={() => setToolForm(f => ({ ...f, features: f.features.filter((_, j) => j !== i) }))}
                        className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                    )}
                  </div>
                ))}
              </div>

              {/* Use Cases */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-600 font-dm-sans">Use Cases</label>
                  <button type="button" onClick={() => setToolForm(f => ({ ...f, use_cases: [...f.use_cases, ""] }))}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"><Plus size={12} /> Add</button>
                </div>
                {toolForm.use_cases.map((uc, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input type="text" placeholder="Use case" value={uc} onChange={e => {
                      const u = [...toolForm.use_cases]; u[i] = e.target.value; setToolForm(prev => ({ ...prev, use_cases: u }));
                    }} className="flex-1 border border-gray-300 px-2 py-1.5 text-xs font-dm-sans outline-none focus:border-black" />
                    {toolForm.use_cases.length > 1 && (
                      <button type="button" onClick={() => setToolForm(f => ({ ...f, use_cases: f.use_cases.filter((_, j) => j !== i) }))}
                        className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex gap-2 pt-2">
                <button type="submit" className="px-4 py-2 bg-black text-white text-sm font-medium hover:bg-gray-800 flex items-center gap-1.5" data-testid="save-tool-btn">
                  <Save size={14} /> {editingTool ? "Update Tool" : "Create Tool"}
                </button>
                <button type="button" onClick={() => { setShowToolForm(false); resetToolForm(); }} className="px-4 py-2 text-gray-500 text-sm hover:text-gray-700 flex items-center gap-1.5">
                  <X size={14} /> Cancel
                </button>
              </div>
            </form>
          )}

          {/* Tools List */}
          <div className="space-y-3">
            {catalogTools.length === 0 ? (
              <div className="bg-white border border-gray-200 p-8 text-center">
                <Cpu size={32} className="text-gray-300 mx-auto mb-2" />
                <p className="text-gray-400 text-sm font-dm-sans">No AI tools added yet</p>
              </div>
            ) : catalogTools.map(tool => (
              <div key={tool.id} className="bg-white border border-gray-200 p-4" data-testid={`admin-tool-${tool.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-gray-900 font-outfit">{tool.name}</h4>
                      <span className={`text-[10px] px-2 py-0.5 font-medium ${
                        tool.status === "live" ? "bg-green-50 text-green-700" :
                        tool.status === "beta" ? "bg-purple-50 text-purple-700" :
                        "bg-amber-50 text-amber-700"
                      }`}>{tool.status}</span>
                      <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-500">{tool.category}</span>
                      {!tool.is_visible && <span className="text-[10px] px-2 py-0.5 bg-red-50 text-red-500">Hidden</span>}
                    </div>
                    <p className="text-xs text-gray-500 font-dm-sans">{tool.tagline}</p>
                    <p className="text-xs text-gray-400 font-dm-sans mt-1">{(tool.features || []).length} features &middot; {(tool.use_cases || []).length} use cases {tool.tool_url ? `· ${tool.tool_url}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button onClick={() => toggleToolVisibility(tool)} className={`p-1.5 rounded ${tool.is_visible ? "text-green-600 hover:bg-green-50" : "text-gray-400 hover:bg-gray-100"}`} title={tool.is_visible ? "Visible" : "Hidden"}>
                      {tool.is_visible ? <Eye size={15} /> : <EyeOff size={15} />}
                    </button>
                    <button onClick={() => openEditTool(tool)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" data-testid={`edit-tool-${tool.id}`}>
                      <Edit size={15} />
                    </button>
                    <button onClick={() => deleteTool(tool.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" data-testid={`delete-tool-${tool.id}`}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subscriptions Tab */}
      {activeTab === "subscriptions" && (
        <div className="bg-white border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 font-outfit">All Subscriptions ({subscriptions.length})</h3>
          </div>
          {subscriptions.length === 0 ? (
            <div className="p-8 text-center">
              <CreditCard size={32} className="text-gray-300 mx-auto mb-2" />
              <p className="text-gray-400 text-sm font-dm-sans">No subscriptions yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="subscriptions-table">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase font-dm-sans">User</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase font-dm-sans">Plan</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase font-dm-sans">Billing</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase font-dm-sans">Amount</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase font-dm-sans">Status</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase font-dm-sans">Expires</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase font-dm-sans">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {subscriptions.map((sub, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="text-gray-900 font-dm-sans">{sub.user_email}</p>
                        <p className="text-xs text-gray-400">{sub.user_id}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="capitalize font-medium text-gray-900 font-dm-sans">{sub.plan_name || sub.plan_id}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 font-dm-sans capitalize">{sub.billing_cycle}</td>
                      <td className="px-4 py-3 text-gray-900 font-dm-sans font-medium">₹{(sub.amount || 0).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 font-medium ${
                          sub.status === "active" ? "bg-green-50 text-green-700" :
                          sub.status === "expired" ? "bg-red-50 text-red-600" :
                          sub.status === "cancelled" ? "bg-yellow-50 text-yellow-700" :
                          "bg-gray-100 text-gray-500"
                        }`}>{sub.status}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs font-dm-sans">{sub.expires_at?.split("T")[0] || "—"}</td>
                      <td className="px-4 py-3">
                        {sub.status === "active" && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => extendSubscription(sub.user_id)}
                              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                              data-testid={`extend-${sub.user_id}`}
                            >
                              <PlusCircle size={12} /> Extend
                            </button>
                            <button
                              onClick={() => cancelSubscription(sub.user_id)}
                              className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                              data-testid={`cancel-${sub.user_id}`}
                            >
                              <XCircle size={12} /> Cancel
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}


      {/* Coupons Tab */}
      {activeTab === "coupons" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 font-outfit">Coupon Codes ({coupons.length})</h3>
            <button
              onClick={() => setShowCouponForm(!showCouponForm)}
              className="flex items-center gap-1.5 px-3 py-2 bg-black text-white text-xs font-medium hover:bg-gray-800 transition-colors"
              data-testid="create-coupon-btn"
            >
              <Plus size={14} /> Create Coupon
            </button>
          </div>

          {/* Create Coupon Form */}
          {showCouponForm && (
            <form onSubmit={createCoupon} className="bg-white border border-gray-200 p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1 font-dm-sans">Code</label>
                  <input
                    type="text"
                    value={couponForm.code}
                    onChange={e => setCouponForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                    placeholder="e.g., LAUNCH50"
                    className="w-full border border-gray-300 px-3 py-2 text-sm font-dm-sans outline-none focus:border-black"
                    required
                    data-testid="coupon-code-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1 font-dm-sans">Discount %</label>
                  <input
                    type="number"
                    min="1" max="100"
                    value={couponForm.discount_percentage}
                    onChange={e => setCouponForm(f => ({ ...f, discount_percentage: e.target.value }))}
                    className="w-full border border-gray-300 px-3 py-2 text-sm font-dm-sans outline-none focus:border-black"
                    required
                    data-testid="coupon-discount-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1 font-dm-sans">Max Uses (blank = unlimited)</label>
                  <input
                    type="number"
                    min="1"
                    value={couponForm.max_uses}
                    onChange={e => setCouponForm(f => ({ ...f, max_uses: e.target.value }))}
                    placeholder="Unlimited"
                    className="w-full border border-gray-300 px-3 py-2 text-sm font-dm-sans outline-none focus:border-black"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1 font-dm-sans">Valid Until (blank = forever)</label>
                  <input
                    type="date"
                    value={couponForm.valid_until}
                    onChange={e => setCouponForm(f => ({ ...f, valid_until: e.target.value }))}
                    className="w-full border border-gray-300 px-3 py-2 text-sm font-dm-sans outline-none focus:border-black"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2 font-dm-sans">Applicable Plans (none = all plans)</label>
                <div className="flex gap-2">
                  {["starter", "professional", "enterprise"].map(plan => (
                    <button
                      key={plan}
                      type="button"
                      onClick={() => togglePlan(plan)}
                      className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                        couponForm.applicable_plans.includes(plan) ? "bg-black text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {plan}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <button type="submit" className="px-4 py-2 bg-black text-white text-sm font-medium hover:bg-gray-800" data-testid="save-coupon-btn">
                  Create Coupon
                </button>
                <button type="button" onClick={() => setShowCouponForm(false)} className="px-4 py-2 text-gray-500 text-sm hover:text-gray-700">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Coupons Table */}
          <div className="bg-white border border-gray-200">
            {coupons.length === 0 ? (
              <div className="p-8 text-center">
                <Tag size={32} className="text-gray-300 mx-auto mb-2" />
                <p className="text-gray-400 text-sm font-dm-sans">No coupons created yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="coupons-table">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase font-dm-sans">Code</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase font-dm-sans">Discount</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase font-dm-sans">Used / Max</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase font-dm-sans">Plans</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase font-dm-sans">Valid Until</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase font-dm-sans">Status</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase font-dm-sans">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {coupons.map((c) => (
                      <tr key={c.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono font-bold text-gray-900">{c.code}</td>
                        <td className="px-4 py-3 text-gray-900 font-medium">{c.discount_percentage}%</td>
                        <td className="px-4 py-3 text-gray-600">{c.times_used || 0} / {c.max_uses || "Unlimited"}</td>
                        <td className="px-4 py-3">
                          {c.applicable_plans ? (
                            <div className="flex flex-wrap gap-1">
                              {c.applicable_plans.map(p => (
                                <span key={p} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 capitalize">{p}</span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">All plans</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{c.valid_until?.split("T")[0] || "No expiry"}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 font-medium ${c.is_active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                            {c.is_active ? "Active" : "Disabled"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => toggleCouponActive(c.id, c.is_active)}
                              className={`text-xs ${c.is_active ? "text-amber-600 hover:text-amber-800" : "text-green-600 hover:text-green-800"}`}
                              data-testid={`toggle-coupon-${c.id}`}
                            >
                              {c.is_active ? "Disable" : "Enable"}
                            </button>
                            <button
                              onClick={() => deleteCoupon(c.id)}
                              className="text-xs text-red-500 hover:text-red-700 flex items-center gap-0.5"
                              data-testid={`delete-coupon-${c.id}`}
                            >
                              <Trash2 size={11} /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Usage & Users Tab */}
      {activeTab === "usage" && stats && (
        <div className="space-y-4">
          {/* Top Users */}
          <div className="bg-white border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 font-outfit">Top Users by Analyses</h3>
            </div>
            {(stats.top_users || []).length === 0 ? (
              <div className="p-8 text-center">
                <Users size={32} className="text-gray-300 mx-auto mb-2" />
                <p className="text-gray-400 text-sm font-dm-sans">No usage data yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase font-dm-sans">#</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase font-dm-sans">User</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase font-dm-sans">Email</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase font-dm-sans">Analyses</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {stats.top_users.map((u, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-400 font-dm-sans">{i + 1}</td>
                        <td className="px-4 py-3 text-gray-900 font-dm-sans font-medium">{u.name || "—"}</td>
                        <td className="px-4 py-3 text-gray-600 font-dm-sans">{u.email}</td>
                        <td className="px-4 py-3">
                          <span className="bg-black text-white text-xs font-bold px-2 py-0.5">{u.analyses}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
